'use client';

import { type EmblaCarouselType } from 'embla-carousel';
import { useState } from 'react';

import { Button } from '@/app/components/button';
import { ButtonGroup } from '@/app/components/button-group';
import { HeaderLevel2 } from '@/app/components/headers/header-level-2';
import { Container } from '@/app/components/layouts/container';
import { Section } from '@/app/components/layouts/section';
import { Image } from '@/app/components/media/image';
import { Sliders } from '@/app/components/sliders';
import { usePaginationSlider } from '@/app/components/sliders/hooks/usePaginationSlider';
import styles from '@/app/cv/components/sections/products/index.module.css';
import {
  CV_PRODUCT_EVENT_GROUP,
  CV_PRODUCT_EVENT_NAME,
} from '@/app/cv/components/sections/products/tracking';
import type { ProductBaseSectionProps } from '@/app/cv/components/sections/products/types';
import { useRevealAnim } from '@/app/hooks/useRevealAnim';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';

export const ProductService = ({
  items = [],
  lead,
  title,
  buttons = [],
  testId = 'product-service',
}: ProductBaseSectionProps) => {
  const [emblaApi, setEmblaApi] = useState<EmblaCarouselType | null>(null);
  const { selectedIndex } = usePaginationSlider(emblaApi);

  useRevealAnim({ defaultBaseDelay: 0.04 });

  return (
    <Section
      size="lg"
      {...createDataAttributes({ test: `${testId}-section` })}
      data-revealanim-group
    >
      <Container className="mb-6 md:mb-10" data-revealanim>
        <HeaderLevel2
          lead={lead}
          title={title || ''}
          theme="light"
          content2ClassName="space-y-2"
          buttons={buttons.map((btn) => ({
            ...btn,
            className: cn('max-lg:hidden', btn.className),
          }))}
        />
      </Container>

      <Container className="relative" data-revealanim>
        <Sliders
          onEmblaInit={setEmblaApi}
          align="center"
          containerClassName="gap-x-4 lg:-ml-6 sm:gap-x-6 md:gap-x-10 xl:ml-0"
          hidden={false}
          emblaOptions={{
            breakpoints: {
              '(min-width: 1122px)': {
                active: false,
              },
            },
          }}
        >
          {items.map((item, index) => {
            const itemButtons = item.buttons || [];
            return (
              <div
                key={index}
                className={cn(
                  styles.slideMaxXs,
                  'group relative flex w-full min-w-0 shrink-0 basis-78 cursor-pointer flex-col sm:basis-142'
                )}
                {...createDataAttributes({
                  test: `slide-${index}`,
                  active: index === selectedIndex ? true : undefined,
                })}
              >
                {!!item.image && (
                  <Image
                    className="rounded-base-md overflow-hidden"
                    src={item.image.src}
                    alt={item.image.alt}
                    width={640}
                    height={480}
                    aspectRatio="aspect-4-3"
                    srcSetOptions={{ crop: '4:3' }}
                    sizes="(min-width: 1024px) 640px, (min-width: 768px) 480px, 400px"
                    hoverEffect="zoom"
                    isGroupHover
                  />
                )}
                <h3
                  className="text-sub-h2 font-noto-bold where-span:text-inline-wrap mt-6"
                  dangerouslySetInnerHTML={{ __html: item.title || '' }}
                />
                {!!item.description && (
                  <p
                    className="text-body-1 mt-2 text-gray-700"
                    dangerouslySetInnerHTML={{ __html: item.description }}
                  />
                )}
                {itemButtons.length > 0 && (
                  <ButtonGroup
                    className="mt-auto pt-2"
                    {...createDataAttributes({
                      test: 'sliders-button-group',
                    })}
                  >
                    {itemButtons.map((button, index) => (
                      <Button
                        key={index}
                        {...button}
                        className={cn('justify-start px-0', button.className)}
                        size="sm"
                        color="black"
                        variant="tertiary"
                        {...createDataAttributes({ test: `button-${index}` })}
                        {...createDataGA({
                          eventName: CV_PRODUCT_EVENT_NAME,
                          group: CV_PRODUCT_EVENT_GROUP.SERVICE,
                          click: true,
                          text: button.children,
                          url: button.href,
                        })}
                      >
                        {button.children}
                      </Button>
                    ))}
                  </ButtonGroup>
                )}
              </div>
            );
          })}
        </Sliders>

        {buttons.length > 0 && (
          <ButtonGroup
            className="mt-6 md:mt-10 lg:hidden"
            {...createDataAttributes({ test: `${testId}-button-group` })}
          >
            {buttons.map((button, index) => (
              <Button
                key={index}
                {...button}
                width="full"
                {...createDataAttributes({ test: `button-${index}` })}
                {...createDataGA({
                  eventName: CV_PRODUCT_EVENT_NAME,
                  group: CV_PRODUCT_EVENT_GROUP.SERVICE,
                  click: true,
                  text: button.children,
                  url: button.href,
                })}
              />
            ))}
          </ButtonGroup>
        )}
      </Container>
    </Section>
  );
};
